import { useEffect, useMemo, useState } from "react";
import adminApi from "./axiosAdmin";
import { StatusBadge } from "./components/StatusBadge";
import { 
  Activity, 
  RefreshCw, 
  TrendingUp, 
  DollarSign, 
  Truck, 
  Users, 
  Store, 
  ShoppingBag, 
  AlertTriangle, 
  CheckCircle,
  Package
} from "lucide-react";

export default function AdminDashboard() {
  // ---------------------------------------------------------------------------
  // 🧠 LOGIQUE MÉTIER (INTACTE - 100% CONSERVÉE)
  // ---------------------------------------------------------------------------
  const [stats, setStats] = useState(null);
  const [anomalies, setAnomalies] = useState([]);
  const [health, setHealth] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchDashboard = async (isRefresh = false) => {
    try {
        if (isRefresh) setRefreshing(true);
        else setLoading(true);

        const [statsRes, anomaliesRes, healthRes] = await Promise.all([
        adminApi.get("/admin/dashboard/stats"),
        adminApi.get("/admin/orders/anomalies"),
        adminApi.get("/admin/dashboard/health"),
        ]);

        // ======================
        // STATS GLOBALES
        // ======================
        setStats(statsRes.data);
        setAnomalies(anomaliesRes.data.stuckOrders || []);
        setHealth(healthRes.data);

        // ======================
        // 💰 REVENU PLATEFORME (TOTAL + MENSUEL)
        // ======================
        const platformRevenue = statsRes.data?.platformRevenue || {};

        setRevenue({
          serviceFeeTotal: Number(platformRevenue.serviceFee?.total) || 0,
          serviceFeeMonthly: Number(platformRevenue.serviceFee?.monthly) || 0,
          deliveryFeeTotal: Number(platformRevenue.deliveryFee?.total) || 0,
          deliveryFeeMonthly: Number(platformRevenue.deliveryFee?.monthly) || 0,
        });

        setLastUpdate(new Date());
    } catch (err) {
        console.error("❌ Dashboard admin:", err);
        setStats(null);
    } finally {
        setLoading(false);
        setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDashboard(false);
  }, []);

  const executive = useMemo(() => {
    if (!stats || !health) return null;

    const stuck = health.indicators?.stuckOrders ?? anomalies.length;
    const suspended =
      health.indicators?.suspendedMerchants ??
      stats.merchants?.suspended ??
      0;
    const pending =
      health.indicators?.pendingOrders ?? stats.orders?.pending ?? 0;

    if (health.status === "healthy") {
      return {
        title: "Système Stable",
        subtitle: "Tous les indicateurs sont au vert.",
        tone: "success"
      };
    }

    if (health.status === "warning") {
      return {
        title: "Attention Requise",
        subtitle: stuck > 0
            ? "Des commandes semblent bloquées."
            : pending > 0
            ? "Volume de commandes en attente élevé."
            : suspended > 0
            ? "Vérifier les marchands suspendus."
            : "Signaux faibles détectés.",
        tone: "warning"
      };
    }

    return {
      title: "Risque Critique",
      subtitle: stuck > 0
          ? "Intervention immédiate : commandes bloquées."
          : "Incident opérationnel majeur.",
      tone: "danger"
    };
  }, [stats, health, anomalies]);

  // ---------------------------------------------------------------------------
  // 🎨 RENDU VISUEL (REFONTE PREMIUM)
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-gray-400">
            <RefreshCw className="animate-spin mb-4 text-[#f34141]" size={32} />
            <p className="font-medium">Chargement du cockpit...</p>
        </div>
    );
  }

  if (!stats) {
    return (
      <div className="p-10 text-center">
        <div className="inline-block p-6 text-red-600 border border-red-100 bg-red-50 rounded-2xl">
            <AlertTriangle className="mx-auto mb-2" size={32} />
            <p className="font-bold">Impossible de charger les données.</p>
            <button onClick={() => fetchDashboard(true)} className="mt-4 underline hover:text-red-800">Réessayer</button>
        </div>
      </div>
    );
  }

  // Couleurs dynamiques pour la carte Santé
  const healthTheme = health?.status === "healthy"
      ? { bg: "bg-emerald-50", border: "border-emerald-100", icon: "text-emerald-600", text: "text-emerald-900" }
      : health?.status === "warning"
      ? { bg: "bg-amber-50", border: "border-amber-100", icon: "text-amber-600", text: "text-amber-900" }
      : { bg: "bg-red-50", border: "border-red-100", icon: "text-red-600", text: "text-red-900" };

  return (
    <div className="min-h-screen p-8 space-y-10 font-sans bg-gray-50/50">
      
      {/* 1. EN-TÊTE COCKPIT */}
      <div className="flex flex-col justify-between gap-4 pb-6 border-b border-gray-200 md:flex-row md:items-end">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-gray-900">
            Dashboard
          </h1>
          <p className="flex items-center mt-2 font-medium text-gray-500">
            <Activity size={18} className="mr-2 text-[#f34141]" /> 
            Vue exécutive & pilotage temps réel
          </p>
        </div>

        <div className="flex items-center gap-4">
            <div className="hidden text-right sm:block">
                <p className="text-xs font-semibold tracking-wider text-gray-400 uppercase">Dernière synchro</p>
                <p className="font-mono text-sm font-bold text-gray-700">
                    {lastUpdate ? lastUpdate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : "--:--"}
                </p>
            </div>
          
            <button
                onClick={() => fetchDashboard(true)}
                disabled={refreshing}
                className={`p-3 rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition-all duration-200 text-gray-700 ${refreshing ? 'opacity-50 cursor-not-allowed' : 'hover:border-gray-300'}`}
                title="Actualiser les données"
            >
                <RefreshCw size={20} className={refreshing ? "animate-spin" : ""} />
            </button>
        </div>
      </div>

      {/* 2. SECTION SANTÉ (ENGINE STATUS) */}
      {health && (
        <section className={`rounded-3xl border ${healthTheme.border} ${healthTheme.bg} p-6 md:p-8 transition-all duration-300 shadow-sm`}>
            <div className="flex flex-col items-start gap-8 lg:flex-row">
                
                {/* Indicateur Principal */}
                <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                        {health.status === 'healthy' ? <CheckCircle className={healthTheme.icon} size={28} /> : <AlertTriangle className={healthTheme.icon} size={28} />}
                        <h2 className={`text-xl font-bold ${healthTheme.text}`}>État du système : {health.level}</h2>
                        <StatusBadge
                            active={health.status === "healthy"}
                            activeLabel="Stable"
                            inactiveLabel="Instable"
                        />
                    </div>
                    {executive && (
                        <div className="space-y-1">
                            <p className="text-lg font-bold text-gray-800">{executive.title}</p>
                            <p className="text-gray-600">{executive.subtitle}</p>
                        </div>
                    )}
                </div>

                {/* Mini-Tuiles de Risque */}
                <div className="flex flex-wrap w-full gap-4 lg:w-auto">
                    <MiniRiskCard
                        label="Commandes Bloquées"
                        value={health.indicators.stuckOrders}
                        isZeroGood={true}
                    />
                    <MiniRiskCard
                        label="Marchands Suspendus"
                        value={health.indicators.suspendedMerchants}
                        isZeroGood={true}
                    />
                    <MiniRiskCard
                        label="Commandes en Attente"
                        value={health.indicators.pendingOrders}
                        isZeroGood={true}
                    />
                </div>
            </div>
        </section>
      )}

      {/* 3. PERFORMANCE FINANCIÈRE */}
      {revenue && (
        <section>
            <h3 className="mb-4 text-sm font-bold tracking-widest text-gray-400 uppercase">
            Performance Financière
            </h3>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
            <MetricCard 
              title="Frais de service (Total)" 
              value={`${revenue.serviceFeeTotal} MRU`} 
              icon={DollarSign}
              accentColor="text-gray-900"
            />

            <MetricCard 
              title="Frais de service (Mois en cours)" 
              value={`${revenue.serviceFeeMonthly} MRU`} 
              icon={TrendingUp}
              accentColor="text-[#f34141]"
            />

            <MetricCard 
              title="Frais de livraison (Total)" 
              value={`${revenue.deliveryFeeTotal} MRU`} 
              icon={Truck}
              accentColor="text-blue-600"
            />

            <MetricCard 
              title="Frais de livraison (Mois en cours)" 
              value={`${revenue.deliveryFeeMonthly} MRU`} 
              icon={Truck}
              accentColor="text-blue-500"
            />
            </div>
        </section>
      )}

      {/* 4. MÉTRIQUES OPÉRATIONNELLES */}
      <section>
        <h3 className="mt-8 mb-4 text-sm font-bold tracking-widest text-gray-400 uppercase">
            Métriques Opérationnelles
        </h3>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5">
            <MetricCard
            title="Commandes Aujourd’hui"
            value={stats.orders.today}
            icon={Package}
            accentColor="text-[#f34141]"
            />

            <MetricCard
            title="Utilisateurs Totaux"
            value={stats.users}
            icon={Users}
            />

            <MetricCard
            title="Marchands (Total)"
            value={stats.merchants.total}
            icon={Store}
            />

            <MetricCard
            title="Marchands Actifs"
            value={stats.merchants.active}
            icon={CheckCircle}
            accentColor="text-green-600"
            />

            <MetricCard
            title="Volume Commandes"
            value={stats.orders.total}
            icon={ShoppingBag}
            />
        </div>
      </section>

      {/* 5. TUNNEL DE COMMANDE (PIPELINE) */}
      <section>
        <h3 className="mt-8 mb-4 text-sm font-bold tracking-widest text-gray-400 uppercase">Tunnel de Commande (Live)</h3>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
            <PipelineCard label="En Attente" value={stats.orders.pending} color="bg-yellow-50 text-yellow-700 border-yellow-200" />
            <PipelineCard label="En Préparation" value={stats.orders.preparing} color="bg-blue-50 text-blue-700 border-blue-200" />
            <PipelineCard label="Prêtes" value={stats.orders.ready} color="bg-indigo-50 text-indigo-700 border-indigo-200" />
            <PipelineCard label="Livrées" value={stats.orders.delivered} color="bg-green-50 text-green-700 border-green-200" />
            <PipelineCard label="Annulées" value={stats.orders.cancelled} color="bg-gray-50 text-gray-500 border-gray-200" />
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 🧱 COMPOSANTS UI INTERNES (POUR LE LOOK "COCKPIT")
// ---------------------------------------------------------------------------

// Carte Métrique Principale (Nouveau Design Luxe)
function MetricCard({ title, value, icon: Icon, accentColor = "text-gray-600" }) {
    return (
        <div className="bg-white p-6 rounded-2xl shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-gray-100 flex items-start justify-between hover:shadow-md transition-shadow duration-200">
            <div>
                <p className="mb-1 text-sm font-semibold text-gray-500">{title}</p>
                <h4 className="text-2xl font-extrabold tracking-tight text-gray-900">{value}</h4>
            </div>
            {Icon && (
                <div className={`p-3 rounded-xl bg-gray-50 ${accentColor}`}>
                    <Icon size={22} strokeWidth={2} />
                </div>
            )}
        </div>
    );
}

// Petite tuile pour les risques (Design épuré)
function MiniRiskCard({ label, value, isZeroGood = true }) {
  const isBad = isZeroGood && value > 0;
  
  return (
    <div className={`flex flex-col justify-center px-5 py-4 rounded-2xl border min-w-[140px] bg-white ${isBad ? 'border-red-200 shadow-sm shadow-red-50' : 'border-gray-100/50'}`}>
      <span className={`text-2xl font-black ${isBad ? 'text-red-600' : 'text-gray-800'}`}>
        {value}
      </span>
      <span className="mt-1 text-xs font-bold leading-tight text-gray-400 uppercase">{label}</span>
    </div>
  );
}

// Carte pour le tunnel de commande (Pipeline)
function PipelineCard({ label, value, color }) {
    return (
        <div className={`p-4 rounded-2xl border flex flex-col items-center justify-center text-center ${color}`}>
            <span className="mb-1 text-2xl font-extrabold">{value}</span>
            <span className="text-xs font-bold opacity-80">{label}</span>
        </div>
    );
}

